const TRACKED_UTM_VALUES = ['utm_source','utm_medium','utm_campaign','utm_content'];

export class Analytics {
    static #doGtag(eventName, data = {}) {
        if (window.gtag) {
            const utmValues = {};
            for (let trackedUtm of TRACKED_UTM_VALUES) {
                utmValues[trackedUtm] = getUtmValue(trackedUtm);
            }

            window.gtag("event", eventName, {
                ...data,
                utmValues: utmValues,
            });
        }

        //console.log(eventName,JSON.stringify(data));
    }

    static addPaymentInfo() {
        Analytics.#doGtag("add_payment_info", {
            payment_type: "Credit Card",
            currency: 'AUD',
            value: 0,
            items: []
        });
    }

    static beginCheckout(data) {
        Analytics.#doGtag("begin_checkout", {
            currency: 'AUD',
            value: parseFloat(data.price),
            items: data.items,
            donationValue: parseFloat(data.donationValue),
            ticketValue: parseFloat(data.ticketValue),
            voucherValue: parseFloat(data.voucherValue),
        });
    }

    static confirmCheckout(data) {
        const pathMatches = document.referrer.match(/\/\/([^\/]+)([^?#]+)/);
        let userAction = 'unknown';
        let referrerPage;

        if (pathMatches && pathMatches.length === 3) {
            referrerPage = pathMatches[2];
        }

        if (referrerPage === '/cart') {
            userAction = 'user already logged in';
        } else if (referrerPage === '/account/create') {
            userAction = 'user signed up';
        } else if (referrerPage === '/account/login') {
            userAction = 'user logged in';
        }

        Analytics.#doGtag("confirm_checkout", {
            currency: 'AUD',
            value: parseFloat(data.price),
            items: data.items,
            donationValue: parseFloat(data.donationValue),
            ticketValue: parseFloat(data.ticketValue),
            voucherValue: parseFloat(data.voucherValue),
            userAction: userAction
        });
    }

    static completePurchase(orderId, orderTotal, ticketTotal, donationTotal, voucherTotal, orderItems, orderPromoCode) {
        Analytics.#doGtag("purchase", {
            transaction_id: orderId,
            currency: 'AUD',
            value: parseFloat(orderTotal),
            items: orderItems,
            donationValue: parseFloat(donationTotal),
            ticketValue: parseFloat(ticketTotal),
            voucherValue: parseFloat(voucherTotal),
            coupon: orderPromoCode,
        });
    }

    static viewCart(data) {
        Analytics.#doGtag("view_cart", {
            currency: 'AUD',
            value: parseFloat(data.price),
            items: data.items,
            donationValue: parseFloat(data.donationValue),
            ticketValue: parseFloat(data.ticketValue),
            voucherValue: parseFloat(data.voucherValue),
        });
    }

    static viewEvent(data) {
        Analytics.#doGtag("view_item", {
            items: data.items
        });
    }

    // viewedPage = support-us || cart
    // only fire if no donation in cart
    static viewDonation() {
        const viewedPage = window.location.pathname.substring(1);

        Analytics.#doGtag("view_item", {
            items: [
                {
                    'item_name': 'Donation',
                    'item_category': 'Donation',
                    'item_category2': viewedPage,
                }
            ]
        });
    }

    static viewVouchers(data) {
        Analytics.#doGtag("view_item", {
            items: data.items
        });
    }

    static userLogin() {
        Analytics.#doGtag("login");
    }

    static userSignUp() {
        Analytics.#doGtag("sign_up");
    }

    // Send an add/remove on partial or full amount as required
    static updateCartDonation(previousAmount, newAmount, pathway) {
        const donationDiff = newAmount - previousAmount;

        if (donationDiff < 0) {
            this.#removeCartDonation(Math.abs(donationDiff), pathway);
        } else if (donationDiff > 0) {
            this.#addCartDonation(Math.abs(donationDiff), pathway);
        }
    }

    static #addCartDonation(amount, pathway) {
        amount = parseFloat(amount);

        Analytics.#doGtag('add_to_cart', {
            currency: 'AUD',
            value: amount,
            items: [
                {
                    item_name: 'Donation',
                    item_category: 'Donation',
                    item_category2: pathway,
                    price: amount,
                    quantity: 1,
                }
            ]
        });
    }

    static #removeCartDonation(amount, pathway) {
        amount = parseFloat(amount);

        Analytics.#doGtag('remove_from_cart', {
            currency: 'AUD',
            value: amount,
            items: [
                {
                    item_name: 'Donation',
                    item_category: 'Donation',
                    item_category2: pathway,
                    price: amount,
                    quantity: 1,
                }
            ]
        });
    }

    static clearCart(data) {
        Analytics.#doGtag('remove_from_cart', {
            currency: 'AUD',
            value: parseFloat(data.amount ?? 0),
            items: data.items ?? []
        });
    }

    static addBasketItems(data) {
        Analytics.#doGtag('add_to_cart', {
            currency: 'AUD',
            value: parseFloat(data.amount ?? 0),
            items: data.items ?? []
        });
    }

    static removeBasketItems(data) {
        Analytics.#doGtag('remove_from_cart', {
            currency: 'AUD',
            value: parseFloat(data.amount ?? 0),
            items: data.items ?? []
        });
    }
}

function currentTimestamp() {
    return Math.floor((new Date()).getTime() / 1000);
}

function getStoredUtms() {
    let storedUtms = window.localStorage ? window.localStorage.getItem('utm_values') : null;
    if (storedUtms) {
        storedUtms = JSON.parse(storedUtms);
    } else {
        storedUtms = {
            'expires': 0,
        };
    }

    return storedUtms;
}

function storeUtmValues() {
    const params = new URLSearchParams(window.location.search.substring(1));
    let storedUtms = getStoredUtms();

    for (let param of params.keys()) {
        param = param.toLowerCase();

        if (TRACKED_UTM_VALUES.indexOf(param) !== -1) {
            // 30 days from now
            storedUtms['expires'] = currentTimestamp() + (60 * 60 * 24 * 30);
            storedUtms[param] = params.get(param);
        }
    }

    if (window.localStorage) {
        window.localStorage.setItem('utm_values', JSON.stringify(storedUtms));
    }
}

function getUtmValue(key) {
    let response = null;
    let storedUtms = getStoredUtms();

    if ((storedUtms.expires ?? 0) > currentTimestamp()) {
        response = storedUtms[key] ?? null;
    }

    return response;
}

$(document).ready(function () {
    storeUtmValues();

    if (window.AnalyticsData) {
        for (let data of window.AnalyticsData) {
            if (data instanceof Object) {
                switch (data.type) {
                    case 'view-cart':
                        Analytics.viewCart(data.data);
                        break;

                    case 'view-event':
                        Analytics.viewEvent(data.data);
                        break;

                    case 'view-donation':
                        Analytics.viewDonation();
                        break;

                    case 'confirm-checkout':
                        Analytics.confirmCheckout(data.data);
                        break;

                    case 'view-vouchers':
                        Analytics.viewVouchers(data.data);
                        break;

                    default:
                        //console.log(data);
                        break;
                }
            }
        }
    }
});